import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "./store";
import { useEffect, useState } from "react";

export enum FetchableStateStatus {
    Initializing = "initializing",
    Loading = "loading",
    Fetched = "fetched",
    Error = "error",
}

export interface FetchableState<T> {
    data?: T;
    currentRequestId?: string;
    lastFetched?: number;
    error?: any;
}

export function useFetchableState<T>(
    { selector, action }: { selector: (state: any) => FetchableState<T>, action: any },
    expirySecs = 60 * 1000
): { 
    data?: T, 
    status: FetchableStateStatus,
    refresh: () => void 
} {
    const dispatch = useDispatch<AppDispatch>();
    const state = useSelector(selector);
    const lastFetched = state?.lastFetched;
    const [needRefresh, setNeedRefresh] = useState(lastFetched === undefined || Date.now() - lastFetched > expirySecs);

    useEffect(() => {
        if (needRefresh) {
            dispatch(action)
            setNeedRefresh(false)
        }
    }, [action, dispatch, needRefresh])

    let status: FetchableStateStatus;
    if (state?.data === undefined) {
        status = FetchableStateStatus.Initializing;
    } else if (state.error) {
        status = FetchableStateStatus.Error;
    } else if (state.currentRequestId) {
        status = FetchableStateStatus.Loading;
    } else {
        status = FetchableStateStatus.Fetched;
    }

    return {
        data: state?.data,
        status: status,
        refresh: () => setNeedRefresh(true)
    }
}